import { LitElement, html } from "lit";
import { AuthController } from "../controllers/auth-controller";
import { store, persistor } from "../redux/store";

class IsLogged extends LitElement {
  private authController: AuthController;
  private _storeReady = false;

  constructor() {
    super();
    this.authController = new AuthController(this);
  }

  async connectedCallback() {
    super.connectedCallback();
    await this.waitForStore();
    this._storeReady = true;
    this.requestUpdate();
  }

  async waitForStore() {
    if (!persistor || !persistor.getState) {
      console.error('Persistor is not ready!');
      return;
    }

    if (!persistor.getState().bootstrapped) {
      await new Promise<void>((resolve) => {
        const unsubscribe = persistor.subscribe(() => {
          if (persistor.getState().bootstrapped) {
            unsubscribe();
            resolve();
          }
        });
      });
    }
  }

  get slotElement(): Element | null {
    return (
      this.renderRoot
        .querySelector("slot")
        ?.assignedElements({ flatten: true })[0] ?? null
    );
  }

  updated() {
    if (this._storeReady && this.authController.initialized) {
      this.updateSlottedElement();
    }
  }

  firstUpdated() {
    if (this._storeReady && this.authController.initialized) {
      this.updateSlottedElement();
    }

    this.renderRoot
      .querySelector("slot")
      ?.addEventListener("slotchange", () => {
        if (this._storeReady && this.authController.initialized) {
          this.updateSlottedElement();
        }
      });
  }

  private updateSlottedElement() {
    if (!this.slotElement) {
      console.warn("No slot element found");
      return;
    }

    if (!this.authController.initialized) {
      console.debug("AuthController not yet initialized");
      return;
    }

    try {
      // Always set the data attribute for backwards compatibility
      this.slotElement.setAttribute(
        "data-sg-a-context",
        String(this.authController.isLogged)
      );

      this.slotElement.setAttribute(
        "data-sg-isp",
        String(this.authController.user?.isPremium || 0)
      );

      // If it's a custom element (web component), also provide the context
      if (this.slotElement.tagName.includes("-")) {
        // @ts-ignore: adding custom property
        this.slotElement.authContext = {
          isLogged: this.authController.isLogged || false,
          isPremium: Boolean(this.authController.user?.isPremium) || false,
        };

        this.slotElement.dispatchEvent(
          new CustomEvent("auth-context-changed", {
            detail: {
              isLogged: this.authController.isLogged || false,
              isPremium: Boolean(this.authController.user?.isPremium) || false,
            },
            bubbles: true,
            composed: true,
          })
        );
      }
    } catch (error) {
      console.error('Error updating auth context:', error);
    }
  }

  render() {
    return html`<slot></slot>`;
  }
}

if (!customElements.get('sg-a-context')) {
  customElements.define('sg-a-context', IsLogged);
}
